/**
 * Service-auth types — the contract Studio fills, plus the adapter interface
 * each integration implements.
 */

export type ServiceKey =
  | "engage"
  | "engage-workflow"
  | "myqueue"
  | "myqueue-tasks"
  | "logocloud"
  | "transcribe"
  ;

/**
 * Header style emitted by an adapter's getAuthHeaders().
 * Code-level concern, not configuration.
 */
export type AuthHeaderStyle = "bearer" | "raw-id" | "engage-multi";

/**
 * The integration auth config template — the shape Studio returns
 * per connection in connections[].authConfig once the backend is extended.
 */
export interface ConnectionAuthConfig {
  clientId: string;
  hostedUiDomain: string;
  poolId: string;
  region: string;
  scopes?: string[];
  apiKey?: string;
  idpIdentifier?: string;
  identityProvider?: string;
  tokenEndpoint?: string;
  appClientId?: string;
  authorityUrl?: string;
  redirectUri?: string;
  requiredScopes?: string;
}

/**
 * The connection item Studio returns, extended with our authConfig contract.
 * Mirrors AddinConnectionViewModel from the backend plus the new field.
 */
export interface ConnectionWithAuth {
  id: string;
  name: string;
  status: string;
  health: string;
  addinKey: string;
  category: string;
  lastHealthCheckAt: string | null;
  moduleLinks: Array<{ moduleId: string; order: number }>;
  authConfig?: ConnectionAuthConfig;
  apiBaseUrl?: string;
}

/**
 * In-memory token snapshot held by an adapter instance. Refresh tokens are
 * persisted separately in localStorage; access/id tokens never are.
 */
export interface TokenSet {
  accessToken: string;
  idToken: string;
  expiresAt: number;
}

export interface ConnectOptions {
  prompt?: "login" | "none" | "consent" | "select_account";
}

export interface ServiceAuthAdapter {
  readonly key: ServiceKey;
  readonly category: string;
  isConnected(): boolean;
  silentConnect(): Promise<TokenSet | null>;
  connect(opts?: ConnectOptions): Promise<TokenSet>;
  refresh(): Promise<TokenSet>;
  getAuthHeaders(): Record<string, string>;
  disconnect(): void;
  onTokenChange(cb: (t: TokenSet | null) => void): () => void;
}

export interface ServiceAuthFactory {
  readonly addinKey: string;
  create(cfg: ConnectionAuthConfig, deps: AdapterDeps): ServiceAuthAdapter;
}

/** Shared dependencies handed to every adapter at construction. */
export interface AdapterDeps {
  storage: TokenStorage;
  dialogRunner: DialogRunner;
  fetchImpl?: typeof fetch;
  now?: () => number;
}

export interface TokenStorage {
  getRefreshToken(key: ServiceKey): string | null;
  setRefreshToken(key: ServiceKey, token: string): void;
  clear(key: ServiceKey): void;
  clearAll(): void;
}

export interface DialogRunner {
  run(authorizeUrl: string, redirectOrigin: string): Promise<DialogResult>;
}

export type DialogResult =
  | { ok: true; code: string; state?: string }
  | { ok: false; error: string; description?: string };

/* ── Errors ──────────────────────────────────── */

export class ServiceNotConfiguredError extends Error {
  constructor(public readonly serviceKey: string) {
    super(`No adapter configured for service "${serviceKey}"`);
    this.name = "ServiceNotConfiguredError";
  }
}

export class ServiceNotConnectedError extends Error {
  constructor(public readonly serviceKey: string) {
    super(`Service "${serviceKey}" is not connected`);
    this.name = "ServiceNotConnectedError";
  }
}

export class ServiceAuthError extends Error {
  constructor(public readonly serviceKey: string, message?: string) {
    super(message ?? `Auth failed for service "${serviceKey}"`);
    this.name = "ServiceAuthError";
  }
}
